<template>
  <v-col>
    <v-card>
      <v-row no-gutters>
        <v-card-title class="headline">
          Energiebilanz
        </v-card-title>
        <v-row no-gutters class="justify-end">
          <div>
            <v-container class="d-flex align-center">
              <v-switch
                v-model="showGroupDetails"
                class="mr-4"
                dense
                hide-details
                inset
                label="Gruppendetails anzeigen"
              />
              <ProjectPageNav />
              <v-btn
                color="success"
                class="mr-4"
                :loading="isExporting"
                :disabled="isExporting || !calcResponse"
                @click="exportEnergyExcel"
                title="Energiebilanz als Excel exportieren"
              >
                <v-icon start>mdi-file-excel</v-icon>
                Excel Export
              </v-btn>
              <!--
                `HelpBar` ist nur die Schublade; geoeffnet wird sie ueber
                `store.showSidebar(<Hilfekomponente>)`. Das fruehere
                `<HelpBar :help="EnergyFlowHelp" />` reichte eine Eigenschaft herein, die
                es an `HelpBar` gar nicht gibt — die Hilfe liess sich auf dieser Seite
                also nie oeffnen. So macht es die Seite Einsparpotential schon immer.
              -->
              <v-tooltip text="Hilfe anzeigen" location="bottom" :disabled="store.sidebarShown">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    color="primary"
                    variant="flat"
                    class="mr-4"
                    @click="store.sidebarShown ? store.hideSidebar() : store.showSidebar(EnergyFlowHelp)"
                  >
                    <v-icon start>
                      {{ store.sidebarShown ? 'mdi-close' : 'mdi-help-circle-outline' }}
                    </v-icon>
                    Hilfe
                  </v-btn>
                </template>
              </v-tooltip>
              <HelpBar />
            </v-container>
          </div>
        </v-row>
      </v-row>

      <div v-if="errorResponse">
        <v-card-text>
          <v-alert type="error" variant="outlined">
            <div>{{ errorResponse }}</div>
          </v-alert>
        </v-card-text>
      </div>

      <div v-if="warningResponse.length">
        <v-card-text>
          <v-alert type="warning" variant="outlined">
            <div v-for="warning in warningResponse" :key="warning">{{ warning }}</div>
          </v-alert>
        </v-card-text>
      </div>

      <div class="pa-4" v-if="!errorResponse">
        <v-card class="chart-section pa-4" variant="outlined">
          <div class="subtitle-1 font-weight-bold mb-2">Energieverbrauch (Baseline, Referenz, Geplant)</div>
          <v-alert v-if="!hasBaseline" type="info" variant="outlined" dense class="mb-3">
            Keine Baseline-Daten vorhanden. Das Einsparpotential wird auf Basis der Referenz- und Geplant-Maßnahmen angezeigt.
          </v-alert>
          <v-table density="comfortable">
            <thead>
              <tr>
                <th>Energieträger</th>
                <th v-if="hasBaseline" class="text-right">Baseline [kWh]</th>
                <th class="text-right">Referenz Maßnahmen [kWh]</th>
                <th class="text-right">Geplant Maßnahmen [kWh]</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in energyConsumptionTableRows" :key="`row-${row.id}`">
                <td>{{ row.name }}</td>
                <td v-if="hasBaseline" class="text-right">{{ formatNumber(row.baseline) }}</td>
                <td class="text-right">{{ formatNumber(row.reference) }}</td>
                <td class="text-right">{{ formatNumber(row.planned) }}</td>
              </tr>
              <tr class="total-row">
                <td>Summe</td>
                <td v-if="hasBaseline" class="text-right">{{ formatNumber(totalBaseline) }}</td>
                <td class="text-right">{{ formatNumber(totalReference) }}</td>
                <td class="text-right">{{ formatNumber(totalPlanned) }}</td>
              </tr>
            </tbody>
          </v-table>
        </v-card>

        <div class="subtitle-1 font-weight-bold mt-4 mb-2">Verbräuche je Energieträger</div>

        <v-row no-gutters>
          <v-col cols="12" md="6" class="pr-md-2">
            <v-card class="chart-section pa-4" variant="outlined">
              <div class="subtitle-1 font-weight-bold mb-2">Verbräuche Referenz</div>
              <v-alert v-if="!perActionEnergyData.length" type="info" variant="outlined" dense>
                Keine berechenbaren Maßnahmenverbräuche verfügbar.
              </v-alert>
              <v-table density="comfortable" v-else>
                <thead>
                  <tr>
                    <th>Maßnahme</th>
                    <th
                      v-for="energy in perActionEnergyColumns"
                      :key="`ref-header-energy-${energy.id}`"
                      class="text-right"
                    >
                      {{ energy.name }} [kWh]
                    </th>
                  </tr>
                </thead>
                <tbody v-for="group in groupedPerActionEnergyData" :key="`ref-group-${group.id}`">
                  <tr class="group-row" @click="toggleGroup(group.id)">
                    <td>
                      <v-icon small>
                        {{ isGroupExpanded(group.id) ? "mdi-chevron-down" : "mdi-chevron-right" }}
                      </v-icon>
                      {{ group.name }}
                    </td>
                    <td
                      v-for="energy in perActionEnergyColumns"
                      :key="`ref-group-${group.id}-energy-${energy.id}`"
                      class="text-right"
                    >
                      {{ formatNumber(getScenarioEnergyValue(group, energy.id, "reference")) }}
                    </td>
                  </tr>
                  <tr
                    v-for="action in group.actions"
                    :key="`ref-group-${group.id}-action-${action.id}`"
                    v-show="isGroupExpanded(group.id)"
                  >
                    <td class="pl-8">{{ action.name }}</td>
                    <td
                      v-for="energy in perActionEnergyColumns"
                      :key="`ref-group-${group.id}-action-${action.id}-energy-${energy.id}`"
                      class="text-right"
                    >
                      {{ formatNumber(getScenarioEnergyValue(action, energy.id, "reference")) }}
                    </td>
                  </tr>
                </tbody>

                <tbody v-if="ungroupedPerActionEnergyData.length">
                  <tr class="group-row">
                    <td :colspan="1 + perActionEnergyColumns.length">Ohne Zuordnung</td>
                  </tr>
                  <tr
                    v-for="action in ungroupedPerActionEnergyData"
                    :key="`ref-ungrouped-action-${action.id}`"
                  >
                    <td class="pl-8">{{ action.name }}</td>
                    <td
                      v-for="energy in perActionEnergyColumns"
                      :key="`ref-ungrouped-action-${action.id}-energy-${energy.id}`"
                      class="text-right"
                    >
                      {{ formatNumber(getScenarioEnergyValue(action, energy.id, "reference")) }}
                    </td>
                  </tr>
                </tbody>

                <tbody>
                  <tr class="total-row">
                    <td>Summe</td>
                    <td
                      v-for="energy in perActionEnergyColumns"
                      :key="`ref-total-energy-${energy.id}`"
                      class="text-right"
                    >
                      {{ formatNumber(Number(totalActionReferenceByEnergy[energy.id]) || 0) }}
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-card>
          </v-col>

          <v-col cols="12" md="6" class="pl-md-2 mt-4 mt-md-0">
            <v-card class="chart-section pa-4" variant="outlined">
              <div class="subtitle-1 font-weight-bold mb-2">Verbräuche Geplant</div>
              <v-alert v-if="!perActionEnergyData.length" type="info" variant="outlined" dense>
                Keine berechenbaren Maßnahmenverbräuche verfügbar.
              </v-alert>
              <v-table density="comfortable" v-else>
                <thead>
                  <tr>
                    <th>Maßnahme</th>
                    <th
                      v-for="energy in perActionEnergyColumns"
                      :key="`plan-header-energy-${energy.id}`"
                      class="text-right"
                    >
                      {{ energy.name }} [kWh]
                    </th>
                  </tr>
                </thead>
                <tbody v-for="group in groupedPerActionEnergyData" :key="`plan-group-${group.id}`">
                  <tr class="group-row" @click="toggleGroup(group.id)">
                    <td>
                      <v-icon small>
                        {{ isGroupExpanded(group.id) ? "mdi-chevron-down" : "mdi-chevron-right" }}
                      </v-icon>
                      {{ group.name }}
                    </td>
                    <td
                      v-for="energy in perActionEnergyColumns"
                      :key="`plan-group-${group.id}-energy-${energy.id}`"
                      class="text-right"
                    >
                      {{ formatNumber(getScenarioEnergyValue(group, energy.id, "planned")) }}
                    </td>
                  </tr>
                  <tr
                    v-for="action in group.actions"
                    :key="`plan-group-${group.id}-action-${action.id}`"
                    v-show="isGroupExpanded(group.id)"
                  >
                    <td class="pl-8">{{ action.name }}</td>
                    <td
                      v-for="energy in perActionEnergyColumns"
                      :key="`plan-group-${group.id}-action-${action.id}-energy-${energy.id}`"
                      class="text-right"
                    >
                      {{ formatNumber(getScenarioEnergyValue(action, energy.id, "planned")) }}
                    </td>
                  </tr>
                </tbody>

                <tbody v-if="ungroupedPerActionEnergyData.length">
                  <tr class="group-row">
                    <td :colspan="1 + perActionEnergyColumns.length">Ohne Zuordnung</td>
                  </tr>
                  <tr
                    v-for="action in ungroupedPerActionEnergyData"
                    :key="`plan-ungrouped-action-${action.id}`"
                  >
                    <td class="pl-8">{{ action.name }}</td>
                    <td
                      v-for="energy in perActionEnergyColumns"
                      :key="`plan-ungrouped-action-${action.id}-energy-${energy.id}`"
                      class="text-right"
                    >
                      {{ formatNumber(getScenarioEnergyValue(action, energy.id, "planned")) }}
                    </td>
                  </tr>
                </tbody>

                <tbody>
                  <tr class="total-row">
                    <td>Summe</td>
                    <td
                      v-for="energy in perActionEnergyColumns"
                      :key="`plan-total-energy-${energy.id}`"
                      class="text-right"
                    >
                      {{ formatNumber(Number(totalActionPlannedByEnergy[energy.id]) || 0) }}
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-card>
          </v-col>
        </v-row>

        <v-row no-gutters class="mt-4">
          <v-col cols="12">
            <v-card class="chart-section pa-4" outlined style="position: relative;">
              <div class="subtitle-1 font-weight-bold mb-2 d-flex align-center">
                <span>Energiebilanz Referenz (Endenergie Maßnahme Nutzenergie)</span>
                <v-tooltip location="bottom" max-width="420">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon
                      x-small
                      color="secondary"
                      class="ml-2"
                      v-bind="props"
                    >
                      <v-icon small>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Zeigt den Energiefluss im Referenzszenario. Links die eingesetzte Endenergie in Kilowattstunden, in der Mitte die Maßnahmen, rechts die erzeugte Nutzenergie. Die Bandbreite entspricht der Energiemenge.</span>
                </v-tooltip>
              </div>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                    style="position: absolute; top: 8px; right: 8px; z-index: 1;"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadSankeyImageByRef('energyRefActionSankey', 'energiebilanz-referenz-massnahmen')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportSankeyDataExcel(referencePerActionSankey, 'energiebilanz-referenz-massnahmen')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <sankey-chart
                v-if="referencePerActionSankey.nodes.length"
                ref="energyRefActionSankey"
                :nodes="referencePerActionSankey.nodes"
                :links="referencePerActionSankey.links"
                :height="Math.max(620, actionResults.length * 72)"
                :total="sankeySumme(referencePerActionSankey)"
                :unit="'kWh'"
              />
              <v-alert v-else type="info" outlined dense>
                Keine Daten für Maßnahmenbezogene Referenz-Energiebilanz verfügbar.
              </v-alert>
            </v-card>
          </v-col>
          <v-col cols="12" class="mt-4">
            <v-card class="chart-section pa-4" outlined style="position: relative;">
              <div class="subtitle-1 font-weight-bold mb-2 d-flex align-center">
                <span>Energiebilanz Geplant (Endenergie Maßnahme Nutzenergie)</span>
                <v-tooltip location="bottom" max-width="420">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon
                      x-small
                      color="secondary"
                      class="ml-2"
                      v-bind="props"
                    >
                      <v-icon small>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Zeigt den Energiefluss im Planszenario nach Umsetzung. Im Vergleich zur Referenz werden Einsparungen über geringere Energiemengen und veränderte Flüsse sichtbar.</span>
                </v-tooltip>
              </div>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                    style="position: absolute; top: 8px; right: 8px; z-index: 1;"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadSankeyImageByRef('energyPlanActionSankey', 'energiebilanz-geplant-massnahmen')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportSankeyDataExcel(plannedPerActionSankey, 'energiebilanz-geplant-massnahmen')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <sankey-chart
                v-if="plannedPerActionSankey.nodes.length"
                ref="energyPlanActionSankey"
                :nodes="plannedPerActionSankey.nodes"
                :links="plannedPerActionSankey.links"
                :height="Math.max(620, actionResults.length * 72)"
                :total="sankeySumme(plannedPerActionSankey)"
                :unit="'kWh'"
              />
              <v-alert v-else type="info" outlined dense>
                Keine Daten für Maßnahmenbezogene Geplant-Energiebilanz verfügbar.
              </v-alert>
            </v-card>
          </v-col>
        </v-row>

        <v-row no-gutters class="mt-4">
          <v-col cols="12" md="6" class="pr-md-2">
            <v-card class="chart-section pa-4" outlined style="position: relative;">
              <div class="subtitle-1 font-weight-bold mb-2 d-flex align-center">
                <span>Energiebilanz Summe Referenz (Endenergie Nutzenergie)</span>
                <v-tooltip location="bottom" max-width="420">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon
                      x-small
                      color="secondary"
                      class="ml-2"
                      v-bind="props"
                    >
                      <v-icon small>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Zeigt die aggregierte Energiebilanz im Referenzszenario als direkte Zuordnung von Endenergie zu Nutzenergie, ohne Maßnahmenebene. Die Bandbreite entspricht dem Energieanteil.</span>
                </v-tooltip>
              </div>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                    style="position: absolute; top: 8px; right: 8px; z-index: 1;"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadSankeyImageByRef('energyRefTotalSankey', 'energiebilanz-referenz-summe')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportSankeyDataExcel(referenceSankey, 'energiebilanz-referenz-summe')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <sankey-chart ref="energyRefTotalSankey" :nodes="referenceSankey.nodes" :links="referenceSankey.links" :height="430"
                :total="sankeySumme(referenceSankey)"
                :unit="'kWh'"
              />
            </v-card>
          </v-col>
          <v-col cols="12" md="6" class="pl-md-2 mt-4 mt-md-0">
            <v-card class="chart-section pa-4" outlined style="position: relative;">
              <div class="subtitle-1 font-weight-bold mb-2 d-flex align-center">
                <span>Energiebilanz Summe Geplant (Endenergie Nutzenergie)</span>
                <v-tooltip location="bottom" max-width="420">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon
                      x-small
                      color="secondary"
                      class="ml-2"
                      v-bind="props"
                    >
                      <v-icon small>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Zeigt die aggregierte Energiebilanz im Planszenario als direkte Zuordnung von Endenergie zu Nutzenergie. Gegenüber der Referenz zeigen schmalere Bänder die erzielten Einsparungen.</span>
                </v-tooltip>
              </div>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                    style="position: absolute; top: 8px; right: 8px; z-index: 1;"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadSankeyImageByRef('energyPlanTotalSankey', 'energiebilanz-geplant-summe')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportSankeyDataExcel(plannedSankey, 'energiebilanz-geplant-summe')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <sankey-chart ref="energyPlanTotalSankey" :nodes="plannedSankey.nodes" :links="plannedSankey.links" :height="430"
                :total="sankeySumme(plannedSankey)"
                :unit="'kWh'"
              />
            </v-card>
          </v-col>
        </v-row>

        <v-row no-gutters class="mt-4" v-if="hasCo2SankeyData">
          <v-col cols="12">
            <v-card class="chart-section pa-4" outlined style="position: relative;">
              <div class="subtitle-1 font-weight-bold mb-2 d-flex align-center">
                <span>CO2e-Bilanz Referenz (Emissionen Maßnahme CO2e)</span>
                <v-tooltip location="bottom" max-width="420">
                  <template v-slot:activator="{ props }">
                    <v-btn icon x-small color="secondary" class="ml-2" v-bind="props">
                      <v-icon small>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Zeigt die CO2e-Emissionen im Referenzszenario in Kilogramm pro Jahr. Die Bandbreite entspricht dem Emissionsbeitrag je Maßnahme.</span>
                </v-tooltip>
              </div>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                    style="position: absolute; top: 8px; right: 8px; z-index: 1;"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadSankeyImageByRef('co2RefActionSankey', 'co2e-bilanz-referenz-massnahmen')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportSankeyDataExcel(referenceCo2PerActionSankey, 'co2e-bilanz-referenz-massnahmen')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <sankey-chart
                v-if="referenceCo2PerActionSankey.nodes.length"
                ref="co2RefActionSankey"
                :nodes="referenceCo2PerActionSankey.nodes"
                :links="referenceCo2PerActionSankey.links"
                :height="Math.max(620, actionResults.length * 72)"
                :total="sankeySumme(referenceCo2PerActionSankey)"
                :unit="'kg'"
              />
              <v-alert v-else type="info" outlined dense>
                Keine Daten für Maßnahmenbezogene Referenz-CO2e-Bilanz verfügbar.
              </v-alert>
            </v-card>
          </v-col>

          <v-col cols="12" class="mt-4">
            <v-card class="chart-section pa-4" outlined style="position: relative;">
              <div class="subtitle-1 font-weight-bold mb-2 d-flex align-center">
                <span>CO2e-Bilanz Geplant (Emissionen Maßnahme CO2e)</span>
                <v-tooltip location="bottom" max-width="420">
                  <template v-slot:activator="{ props }">
                    <v-btn icon x-small color="secondary" class="ml-2" v-bind="props">
                      <v-icon small>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Zeigt die CO2e-Emissionen im Planszenario in Kilogramm pro Jahr. Im Vergleich zur Referenz werden Emissionsminderungen sichtbar.</span>
                </v-tooltip>
              </div>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                    style="position: absolute; top: 8px; right: 8px; z-index: 1;"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadSankeyImageByRef('co2PlanActionSankey', 'co2e-bilanz-geplant-massnahmen')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportSankeyDataExcel(plannedCo2PerActionSankey, 'co2e-bilanz-geplant-massnahmen')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <sankey-chart
                v-if="plannedCo2PerActionSankey.nodes.length"
                ref="co2PlanActionSankey"
                :nodes="plannedCo2PerActionSankey.nodes"
                :links="plannedCo2PerActionSankey.links"
                :height="Math.max(620, actionResults.length * 72)"
                :total="sankeySumme(plannedCo2PerActionSankey)"
                :unit="'kg'"
              />
              <v-alert v-else type="info" outlined dense>
                Keine Daten für Maßnahmenbezogene Geplant-CO2e-Bilanz verfügbar.
              </v-alert>
            </v-card>
          </v-col>
        </v-row>

        <v-row no-gutters class="mt-4" v-if="hasCo2SankeyData">
          <v-col cols="12" md="6" class="pr-md-2">
            <v-card class="chart-section pa-4" outlined style="position: relative;">
              <div class="subtitle-1 font-weight-bold mb-2 d-flex align-center">
                <span>CO2e-Bilanz Summe Referenz</span>
                <v-tooltip location="bottom" max-width="420">
                  <template v-slot:activator="{ props }">
                    <v-btn icon x-small color="secondary" class="ml-2" v-bind="props">
                      <v-icon small>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Zeigt die aggregierten CO2e-Emissionen im Referenzszenario in Kilogramm pro Jahr.</span>
                </v-tooltip>
              </div>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                    style="position: absolute; top: 8px; right: 8px; z-index: 1;"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadSankeyImageByRef('co2RefTotalSankey', 'co2e-bilanz-referenz-summe')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportSankeyDataExcel(referenceCo2Sankey, 'co2e-bilanz-referenz-summe')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <sankey-chart
                v-if="referenceCo2Sankey.nodes.length"
                ref="co2RefTotalSankey"
                :nodes="referenceCo2Sankey.nodes"
                :links="referenceCo2Sankey.links"
                :height="430"
                :total="sankeySumme(referenceCo2Sankey)"
                :unit="'kg'"
              />
            </v-card>
          </v-col>
          <v-col cols="12" md="6" class="pl-md-2 mt-4 mt-md-0">
            <v-card class="chart-section pa-4" outlined style="position: relative;">
              <div class="subtitle-1 font-weight-bold mb-2 d-flex align-center">
                <span>CO2e-Bilanz Summe Geplant</span>
                <v-tooltip location="bottom" max-width="420">
                  <template v-slot:activator="{ props }">
                    <v-btn icon x-small color="secondary" class="ml-2" v-bind="props">
                      <v-icon small>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Zeigt die aggregierten CO2e-Emissionen im Planszenario in Kilogramm pro Jahr.</span>
                </v-tooltip>
              </div>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                    style="position: absolute; top: 8px; right: 8px; z-index: 1;"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadSankeyImageByRef('co2PlanTotalSankey', 'co2e-bilanz-geplant-summe')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportSankeyDataExcel(plannedCo2Sankey, 'co2e-bilanz-geplant-summe')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <sankey-chart
                v-if="plannedCo2Sankey.nodes.length"
                ref="co2PlanTotalSankey"
                :nodes="plannedCo2Sankey.nodes"
                :links="plannedCo2Sankey.links"
                :height="430"
                :total="sankeySumme(plannedCo2Sankey)"
                :unit="'kg'"
              />
            </v-card>
          </v-col>
        </v-row>

        <v-row no-gutters class="mt-4" v-if="hasEnergyMixData">
          <v-col cols="12" md="6" class="pr-md-2">
            <v-card class="chart-section pa-4" outlined style="position: relative;">
              <div class="subtitle-1 font-weight-bold mb-2 d-flex align-center">
                <span>Endenergie-Mix Referenz</span>
                <v-tooltip location="bottom" max-width="420">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon
                      x-small
                      color="secondary"
                      class="ml-2"
                      v-bind="props"
                    >
                      <v-icon small>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Zeigt die Zusammensetzung der Endenergie im Referenzszenario. Jedes Segment steht für den Anteil eines Energieträgers an der gesamten eingesetzten Endenergie.</span>
                </v-tooltip>
              </div>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                    style="position: absolute; top: 8px; right: 8px; z-index: 1;"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadChartImageByRef('energyMixReferenceChart', 'energiebilanz-mix-referenz')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportChartDataExcelGeneric(energyMixReferenceChartData, 'energiebilanz-mix-referenz')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <div class="chart-wrapper donut-wrapper">
                <doughnut-chart
                  ref="energyMixReferenceChart"
                  :chart-data="energyMixReferenceChartData"
                  :options="energyMixChartOptions"
                  :center-text="`Summe ${formatNumber(kreisSumme(energyMixReferenceChartData))} kWh`"
                />
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="6" class="pl-md-2 mt-4 mt-md-0">
            <v-card class="chart-section pa-4" outlined style="position: relative;">
              <div class="subtitle-1 font-weight-bold mb-2 d-flex align-center">
                <span>Endenergie-Mix Geplant</span>
                <v-tooltip location="bottom" max-width="420">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon
                      x-small
                      color="secondary"
                      class="ml-2"
                      v-bind="props"
                    >
                      <v-icon small>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Zeigt die Zusammensetzung der Endenergie im Planszenario. Der Vergleich mit dem Referenz-Mix macht Energieträgerwechsel und Strukturänderungen der Versorgung sichtbar.</span>
                </v-tooltip>
              </div>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                    style="position: absolute; top: 8px; right: 8px; z-index: 1;"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadChartImageByRef('energyMixPlannedChart', 'energiebilanz-mix-geplant')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportChartDataExcelGeneric(energyMixPlannedChartData, 'energiebilanz-mix-geplant')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <div class="chart-wrapper donut-wrapper">
                <doughnut-chart
                  ref="energyMixPlannedChart"
                  :chart-data="energyMixPlannedChartData"
                  :options="energyMixChartOptions"
                  :center-text="`Summe ${formatNumber(kreisSumme(energyMixPlannedChartData))} kWh`"
                />
              </div>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </v-card>

    <!--
      Deckungsgrad vor der Regression.
      Wer die Einsparungen der Maßnahmen von der gemessenen Baseline abziehen
      will, muss zuerst wissen, ob die Maßnahmen überhaupt in der Baseline
      stecken. Die Karte beantwortet das je Energieträger.
    -->
    <v-card v-if="hasBaseline" class="chart-section pa-4 mt-4" variant="outlined">
      <div class="chart-title d-flex align-center">
        <span>Deckungsgrad: Baseline gegen Referenz</span>
        <v-spacer />
        <v-tooltip location="bottom" max-width="460">
          <template v-slot:activator="{ props }">
            <v-btn icon x-small color="secondary" v-bind="props">
              <v-icon small>mdi-information-outline</v-icon>
            </v-btn>
          </template>
          <span>Die Baseline ist gemessen, Referenz und Geplant sind gerechnet. Der
            Deckungsgrad sagt, welchen Anteil des gemessenen Verbrauchs die erfassten
            Maßnahmen abbilden. Unter 100 % ist der Normalfall — die Maßnahmen decken
            einen Teil des Standorts. Über 100 % ist ein Warnzeichen: das Modell
            verbraucht mehr als die Zähler, die Einsparungen sind dann zu hoch
            angesetzt.</span>
        </v-tooltip>
      </div>

      <v-alert
        v-if="coverageOverruns.length"
        type="warning"
        variant="outlined"
        density="compact"
        class="mb-3"
      >
        <div class="font-weight-bold mb-1">
          Die gerechnete Referenz liegt über dem gemessenen Verbrauch.
        </div>
        <div v-for="zeile in coverageOverruns" :key="zeile.id">
          {{ zeile.name }}: Referenz {{ formatNumber(zeile.reference) }} kWh gegen
          Baseline {{ formatNumber(zeile.baseline) }} kWh
          ({{ formatNumber(zeile.coverage) }} %)
        </div>
        <div class="text-caption mt-1">
          Einsparungen, die aus dieser Referenz abgeleitet sind, fallen entsprechend zu
          hoch aus. Prüfen Sie Betriebsstunden und Leistungsangaben der Maßnahmen.
        </div>
      </v-alert>

      <v-table density="compact" class="mb-3">
        <thead>
          <tr>
            <th>Energieträger</th>
            <th class="text-right">Baseline [kWh]</th>
            <th class="text-right">Referenz [kWh]</th>
            <th class="text-right">Deckungsgrad</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="zeile in coverageRows" :key="zeile.id">
            <td>{{ zeile.name }}</td>
            <td class="text-right">{{ formatNumber(zeile.baseline) }}</td>
            <td class="text-right">{{ formatNumber(zeile.reference) }}</td>
            <td
              class="text-right"
              :class="{ 'text-warning font-weight-bold': zeile.coverage > 100 }"
            >
              {{ zeile.baseline > 0 ? `${formatNumber(zeile.coverage)} %` : "-" }}
            </td>
          </tr>
        </tbody>
      </v-table>

      <div class="chart-wrapper">
        <standard-bar-chart
          ref="coverageChart"
          :chart-data="coverageChartData"
          :options="coverageChartOptions"
        />
      </div>
    </v-card>

    <RegressionAnalysis
      :project-id="projectId"
      :savings-by-fuel="savingsByFuelForForecast"
      :co2-savings-by-fuel="co2SavingsByFuel"
      :last-baseline-year="lastBaselineYear"
      class="mt-4"
    />
  </v-col>
</template>

<script>
import SankeyChart from "@/components/calcpreview/SankeyChart.vue";
import DoughnutChart from "@/components/calcpreview/DoughnutChart.vue";
import StandardBarChart from "@/components/calcpreview/StandardBarChart.vue";
import HelpBar from "@/components/HelpBar.vue";
import ProjectPageNav from "@/components/ProjectPageNav.vue";
import RegressionAnalysis from "@/components/RegressionAnalysis.vue";
import EnergyFlowHelp from "@/components/help/EnergyFlowHelp.vue";
import { useProjectStore } from "@/stores/projectStore";
import { storeDocxModuleCharts } from "@/utils/docxAppendixCache";
import { exportChartExcel, exportSankeyExcel } from "@/utils/chartDataExport";
import api from "@/plugins/axios";
import { toast } from "vue3-toastify";

/*
 * Die Ausgangsseite eines Maßnahmenknotens.
 *
 * Hier stand `[10, 11, 12, 16]` — Nutzenergie Strom, Wärme, Kälte und Nutzen.
 * Alles andere Positive fiel heraus, und damit stimmte die Bilanz nicht mehr:
 *
 *   Kesseltausch (Referenz)   3 602 365 hinein, 2 997 576 hinaus
 *   RLT-Ventilatoren          59 658 hinein, 30 224 hinaus
 *   Heizungsnetzpumpen        78 032 hinein, 48 460 hinaus
 *   PV-Dachanlage (Geplant)   380 000 hinein, 0 hinaus
 *
 * Fehlend waren die **Verluste** (Id 15) und bei der PV der erzeugte
 * erneuerbare Strom (21) samt Einspeisung (31). Bei den Ventilatoren waren das
 * 49 % der eingesetzten Energie. Ein Sankey, dessen Knoten mehr aufnimmt als
 * abgibt, ist nicht bloss unvollständig — er ist falsch gezeichnet, und
 * ausgerechnet die Verluste sind das, woran eine Effizienzmaßnahme ansetzt.
 *
 * Gezeichnet wird deshalb **jeder** positive Fluss. Die Balance des
 * Rechenkerns geht per Konstruktion auf (`Balance.energy_check`), damit stimmt
 * auch das Bild.
 */

//: Verluste. Kein Nutzen — bekommen eine eigene Knotenart und damit eine
//: eigene Farbe, damit sie nicht wie ein Ergebnis aussehen.
const SANKEY_LOSS_IDS = [15];

/** Die Knotenart eines Ausgangsflusses. */
function sankeyTargetType(energyId) {
  return SANKEY_LOSS_IDS.includes(Number(energyId)) ? "loss" : "ne";
}

/*
 * Auf welchen Träger ein Posten der CO₂-Aufteilung gehört.
 *
 * Die Aufteilung des Servers führt mehrere Ids, die denselben Träger meinen:
 *
 *  - **Nutzenergie Strom (10)** ist der Strombedarf der Maßnahmen. Physikalisch
 *    derselbe Strom wie die Endenergie (1); `simple_ne_specs` gibt beiden auch
 *    denselben CO₂-Faktor.
 *  - **Erneuerbare Flüsse (21–23)** und **Einspeisungen (31–33)** tragen keinen
 *    eigenen Faktor. Der Rechenkern bewertet sie über den Träger, den sie
 *    ersetzen, und führt sie als eigenen, **negativen** Posten
 *    (`Balance.renewable_displacement`, `Balance.feed_in_credit_mapping`).
 *
 * Zusammengefasst wird deshalb über die **Id**, nicht über den Anzeigenamen.
 * Zwei Anläufe sind an genau dieser Stelle gescheitert: Id 21 heisst
 * „Erneuerbarer Strom" und Id 1 trägt den Katalognamen des Projekts — nach
 * Namen gruppiert fand die PV-Gutschrift ihren Strang nie, wurde als negativer
 * Posten verworfen, und die Karte wies die Emissionen um die Gutschrift zu
 * hoch aus.
 */
/**
 * Die Id, unter der ein Energiefluss gefuehrt wird.
 *
 * Siehe `KANONISCHE_ENERGIE` — fasst insbesondere Nutzenergie Strom (10) und
 * Endenergie Strom (1) zusammen.
 */
function kanonischeId(energyId) {
  const id = Number(energyId);
  return KANONISCHE_ENERGIE[id] ?? id;
}

const KANONISCHE_ENERGIE = {
  10: 1,   // Nutzenergie Strom   -> Strom
  21: 1,   // Erneuerbarer Strom  -> Strom
  31: 1,   // Einspeisung Strom   -> Strom
  22: 11,  // Erneuerbare Wärme   -> Nutzenergie Wärme
  32: 11,  // Einspeisung Wärme   -> Nutzenergie Wärme
  23: 12,  // Erneuerbare Kälte   -> Nutzenergie Kälte
  33: 12,  // Einspeisung Kälte   -> Nutzenergie Kälte
};

export default {
  name: "ProjectEnergyFlow",
  setup() {
    const store = useProjectStore();
    return { store };
  },
  components: {
    SankeyChart,
    DoughnutChart,
    StandardBarChart,
    HelpBar,
    ProjectPageNav,
    RegressionAnalysis,
  },
  data() {
    return {
      EnergyFlowHelp,
      calcResponse: null,
      errorResponse: null,
      warningResponse: [],
      fuels: {},
      actionCalcList: [],
      consMitGens: {},
      showGroupDetails: false,
      collapsedGroupIds: [],
      isExporting: false,
      isDocxExporting: false,
      docxCacheTimer: null,
    };
  },
  computed: {
    projectId() {
      return this.$route.params.projectId;
    },
    project() {
      return this.store.currentProject;
    },
    /*
     * Die selbst benannten Wärmequellen des Projekts (Energieträger 24–26).
     * `/projects/fuels/` liefert nur den Katalognamen, denn „Biogas" gilt für
     * einen Standort und nicht für den Katalog.
     */
    customEnergyNames() {
      const names = {};
      for (const entry of this.project?.energy_data || []) {
        if (entry.custom_name) names[Number(entry.fuel)] = entry.custom_name;
      }
      return names;
    },
    actionList() {
      return this.store.currentProjectActions || [];
    },
    result() {
      return this.calcResponse?.result || {};
    },
    baselineRows() {
      return this.result.baseline || [];
    },
    hasBaseline() {
      return this.baselineRows.length > 0;
    },
    totalResult() {
      return this.result.total || {};
    },
    /*
     * Die Namen, die der Rechenkern zu seinen Fluessen mitschickt.
     *
     * Deckt die Nutzenergien und Umgebungsgroessen ab, die im Traegerkatalog
     * des Projekts nicht vorkommen.
     */
    flowNamesById() {
      const namen = {};
      for (const row of [...this.refEnergyRows, ...this.planEnergyRows]) {
        const id = Number(row?.id);
        if (Number.isFinite(id) && row?.name && !(id in namen)) {
          namen[id] = row.name;
        }
      }
      return namen;
    },
    refEnergyRows() {
      return this.totalResult.ref_energy || [];
    },
    planEnergyRows() {
      return this.totalResult.plan_energy || [];
    },
    /*
     * Die Zeilen der Verbrauchstabelle.
     *
     * Über `kanonischeId`, und das ist eine Fehlerbehebung: die Baseline kommt
     * aus `project.consumptions` und führt **Endenergie**träger (Strom = 1),
     * Referenz und Geplant kommen aus den Flüssen der Maßnahmen und führen
     * den Strombedarf als **Nutzenergie** (Strom = 10). Ohne Zusammenführung
     * stand der Strom zweimal in der Tabelle — einmal mit Baseline und
     * Nullen, einmal mit Referenz/Geplant und ohne Baseline:
     *
     *     Strom   640.000         0         0
     *     Strom         0   700.000   520.000
     *
     * Die Einsparung des Stroms liess sich damit nie gegen die Messung
     * stellen, und der Deckungsgrad darunter wäre sinnlos gewesen.
     */
    eeIdsForTable() {
      const baselineIds = this.baselineRows.map((row) => kanonischeId(row.fuel));
      const referenceIds = this.refEnergyRows
        .filter((row) => Number(row.quantity) < 0)
        .map((row) => kanonischeId(row.id));
      const plannedIds = this.planEnergyRows
        .filter((row) => Number(row.quantity) < 0)
        .map((row) => kanonischeId(row.id));
      return Array.from(new Set([...baselineIds, ...referenceIds, ...plannedIds]))
        .sort((a, b) => a - b);
    },
    baselineByFuel() {
      return this.baselineRows.reduce((acc, row) => {
        const id = kanonischeId(row.fuel);
        acc[id] = (acc[id] || 0) + (Number(row.consumption) || 0);
        return acc;
      }, {});
    },
    referenceByFuel() {
      return this.refEnergyRows.reduce((acc, row) => {
        const id = kanonischeId(row.id);
        const quantity = Number(row.quantity) || 0;
        if (quantity >= 0) {
          return acc;
        }
        acc[id] = (acc[id] || 0) + Math.abs(quantity);
        return acc;
      }, {});
    },
    plannedByFuel() {
      return this.planEnergyRows.reduce((acc, row) => {
        const id = kanonischeId(row.id);
        const quantity = Number(row.quantity) || 0;
        if (quantity >= 0) {
          return acc;
        }
        acc[id] = (acc[id] || 0) + Math.abs(quantity);
        return acc;
      }, {});
    },
    energyConsumptionTableRows() {
      return this.eeIdsForTable.map((id) => {
        const baseline = Number(this.baselineByFuel[id]) || 0;
        const reference = Number(this.referenceByFuel[id]) || 0;
        const planned = Number(this.plannedByFuel[id]) || 0;
        const savings = reference - planned;
        const savingsPercentage = reference > 0 ? (savings / reference) * 100 : 0;
        return {
          id,
          name: this.getEnergyName(id),
          baseline,
          reference,
          planned,
          savings,
          savingsPercentage,
        };
      });
    },
    totalBaseline() {
      return this.energyConsumptionTableRows.reduce((sum, row) => sum + row.baseline, 0);
    },
    totalReference() {
      return this.energyConsumptionTableRows.reduce((sum, row) => sum + row.reference, 0);
    },
    totalPlanned() {
      return this.energyConsumptionTableRows.reduce((sum, row) => sum + row.planned, 0);
    },
    totalSavings() {
      return this.totalReference - this.totalPlanned;
    },
    totalSavingsPercentage() {
      if (this.totalReference <= 0) {
        return 0;
      }
      return (this.totalSavings / this.totalReference) * 100;
    },
    totalActionReference() {
      return Object.values(this.totalActionReferenceByEnergy)
        .reduce((sum, value) => sum + (Number(value) || 0), 0);
    },
    totalActionPlanned() {
      return Object.values(this.totalActionPlannedByEnergy)
        .reduce((sum, value) => sum + (Number(value) || 0), 0);
    },
    perActionEnergyIds() {
      const ids = new Set();
      this.perActionEnergyData.forEach((action) => {
        (action.rows || []).forEach((row) => {
          ids.add(Number(row.id));
        });
      });
      this.refEnergyRows
        .filter((row) => Number(row.quantity) < 0)
        .forEach((row) => {
          ids.add(Number(row.id));
        });
      this.planEnergyRows
        .filter((row) => Number(row.quantity) < 0)
        .forEach((row) => {
          ids.add(Number(row.id));
        });
      return Array.from(ids).sort((a, b) => a - b);
    },
    perActionEnergyColumns() {
      return this.perActionEnergyIds.map((id) => ({
        id,
        name: this.getEnergyName(id),
      }));
    },
    totalActionReferenceByEnergy() {
      return this.mapFlowsById(this.totalResult?.ref_energy || []);
    },
    totalActionPlannedByEnergy() {
      return this.mapFlowsById(this.totalResult?.plan_energy || []);
    },
    totalActionSavings() {
      return this.totalActionReference - this.totalActionPlanned;
    },
    totalActionSavingsPercentage() {
      if (this.totalActionReference <= 0) {
        return 0;
      }
      return (this.totalActionSavings / this.totalActionReference) * 100;
    },
    actionGroups() {
      return this.result.actionGroups || [];
    },
    ungroupedActionResults() {
      return this.result.actions || [];
    },
    groupedActionResults() {
      return this.actionGroups.flatMap((group) => group.actions || []);
    },
    groupedChartActions() {
      return (this.actionGroups || []).map((group) => ({
        id: `group-${group.id}`,
        name: group.name || `Gruppe ${group.id}`,
        energyRef: group.total?.ref_energy || [],
        energyPlan: group.total?.plan_energy || [],
        errors: [],
      }));
    },
    chartActionResults() {
      const base = [...this.groupedChartActions, ...this.ungroupedActionResults];
      if (!this.showGroupDetails) {
        return base;
      }
      return [...this.ungroupedActionResults, ...this.groupedActionResults];
    },
    allActionResults() {
      return [...this.ungroupedActionResults, ...this.groupedActionResults];
    },
    actionResults() {
      return this.chartActionResults;
    },
    perActionEnergyData() {
      return this.allActionResults
        .filter((action) => !Array.isArray(action.errors) || action.errors.length === 0)
        .map((action) => {
          const referenceById = this.mapFlowsById(action.energyRef);
          const plannedById = this.mapFlowsById(action.energyPlan);
          const ids = Array.from(new Set([
            ...Object.keys(referenceById).map(Number),
            ...Object.keys(plannedById).map(Number),
          ]))
            .sort((a, b) => a - b);

          const rows = ids.map((id) => {
            const reference = Number(referenceById[id]) || 0;
            const planned = Number(plannedById[id]) || 0;
            const savings = reference - planned;
            const savingsPercentage = reference > 0 ? (savings / reference) * 100 : 0;

            return {
              id,
              name: this.getEnergyName(id),
              reference,
              planned,
              savings,
              savingsPercentage,
            };
          });

          const totalReference = rows.reduce((sum, row) => sum + row.reference, 0);
          const totalPlanned = rows.reduce((sum, row) => sum + row.planned, 0);
          const totalSavings = totalReference - totalPlanned;
          const totalSavingsPercentage = totalReference > 0 ? (totalSavings / totalReference) * 100 : 0;

          return {
            id: action.id,
            name: action.name,
            rows,
            referenceByEnergy: referenceById,
            plannedByEnergy: plannedById,
            totalReference,
            totalPlanned,
            totalSavings,
            totalSavingsPercentage,
          };
        })
        .filter((action) => action.rows.length > 0);
    },
    perActionEnergyDataById() {
      return this.perActionEnergyData.reduce((acc, action) => {
        acc[action.id] = action;
        return acc;
      }, {});
    },
    groupedPerActionEnergyData() {
      return (this.actionGroups || [])
        .map((group) => ({
          id: group.id,
          name: group.name || `Gruppe ${group.id}`,
          actions: (group.actions || [])
            .map((action) => this.perActionEnergyDataById[action.id])
            .filter(Boolean),
          totalRefEnergy: group.total?.ref_energy || [],
          totalPlanEnergy: group.total?.plan_energy || [],
        }))
        .map((group) => {
          const backendReferenceByEnergy = this.mapFlowsById(group.totalRefEnergy);
          const backendPlannedByEnergy = this.mapFlowsById(group.totalPlanEnergy);

          const fallbackReferenceByEnergy = group.actions.reduce((acc, action) => {
            Object.entries(action.referenceByEnergy || {}).forEach(([id, value]) => {
              acc[id] = (acc[id] || 0) + (Number(value) || 0);
            });
            return acc;
          }, {});

          const fallbackPlannedByEnergy = group.actions.reduce((acc, action) => {
            Object.entries(action.plannedByEnergy || {}).forEach(([id, value]) => {
              acc[id] = (acc[id] || 0) + (Number(value) || 0);
            });
            return acc;
          }, {});

          const referenceByEnergy = Object.keys(backendReferenceByEnergy).length
            ? backendReferenceByEnergy
            : fallbackReferenceByEnergy;
          const plannedByEnergy = Object.keys(backendPlannedByEnergy).length
            ? backendPlannedByEnergy
            : fallbackPlannedByEnergy;

          const totalReference = Object.values(referenceByEnergy)
            .reduce((sum, value) => sum + (Number(value) || 0), 0);
          const totalPlanned = Object.values(plannedByEnergy)
            .reduce((sum, value) => sum + (Number(value) || 0), 0);
          const totalSavings = totalReference - totalPlanned;
          const totalSavingsPercentage = totalReference > 0 ? (totalSavings / totalReference) * 100 : 0;
          return {
            ...group,
            referenceByEnergy,
            plannedByEnergy,
            totalReference,
            totalPlanned,
            totalSavings,
            totalSavingsPercentage,
          };
        })
        .filter((group) => group.actions.length > 0);
    },
    ungroupedPerActionEnergyData() {
      const groupedIds = new Set(
        this.groupedPerActionEnergyData.flatMap((group) => group.actions.map((action) => action.id)),
      );

      return this.perActionEnergyData.filter((action) => !groupedIds.has(action.id));
    },
    cleanActionResults() {
      return this.actionResults.filter(
        (action) => (!Array.isArray(action.errors) || action.errors.length === 0) && !action.isLinkedGenerator,
      );
    },
    totalReferenceCo2() {
      const totalValue = Number(this.totalResult?.ref_co2);
      if (Number.isFinite(totalValue) && totalValue !== 0) {
        return totalValue;
      }
      return this.cleanActionResults.reduce((sum, action) => sum + (Number(action?.ref_co2 ?? action?.total?.ref_co2) || 0), 0);
    },
    totalPlannedCo2() {
      const totalValue = Number(this.totalResult?.plan_co2);
      if (Number.isFinite(totalValue) && totalValue !== 0) {
        return totalValue;
      }
      return this.cleanActionResults.reduce((sum, action) => sum + (Number(action?.plan_co2 ?? action?.total?.plan_co2) || 0), 0);
    },
    /*
     * Der CO₂-Faktor je Energieträger, so wie er im Projekt hinterlegt ist.
     *
     * Dieselbe Grösse, mit der die Rechnung arbeitet: `co2_sum` in
     * `methods/basics.py` summiert über die Endenergieträger das Produkt aus
     * Menge und Faktor. Die Aufteilung unten rechnet deshalb nicht neben der
     * Anwendung her, sondern zerlegt genau deren Summe.
     */
    /*
     * Die Emissionen je Energieträger — fertig vom Server.
     *
     * `ActionResult`/`Total` liefern `ref_co2_energy` und `plan_co2_energy`.
     * Gebildet werden sie wie die Kostenaufteilung daneben: `co2_sum` mit
     * einer einelementigen Energiemenge, also über dieselbe Rechnung wie die
     * Gesamtzahl. Die Teile addieren sich damit **exakt** zur Summe.
     *
     * Diese Seite hat es vorher selbst gerechnet, aus Mengen mal Faktoren.
     * Das konnte nicht aufgehen, und zwar aus zwei Gründen, die man der
     * Grafik nicht ansieht:
     *
     *  1. Der Strombedarf der Maßnahmen kommt als **Nutz**energie Strom
     *     (Id 10) herein, Faktoren hinterlegt ein Projekt aber nur für
     *     **End**energieträger. Der Strom fand keinen Faktor und fehlte —
     *     genau der gemeldete Fehler.
     *  2. Gutschriften für selbst verbrauchten erneuerbaren Strom und für
     *     Einspeisung gehen über `renewable_displacement` und
     *     `feed_in_credit_mapping` in die Summe ein. Von aussen sind sie
     *     nicht nachzubilden; am Demo-Projekt fehlten dadurch 16 %.
     */
    referenceCo2ByFuel() {
      return this.totalResult.ref_co2_energy || {};
    },
    plannedCo2ByFuel() {
      return this.totalResult.plan_co2_energy || {};
    },
    hasCo2SankeyData() {
      return this.totalReferenceCo2 !== 0 || this.totalPlannedCo2 !== 0;
    },
    referenceCo2PerActionSankey() {
      return this.buildCo2PerActionSankeyData("ref_co2_energy", "ref_co2", "Referenz");
    },
    plannedCo2PerActionSankey() {
      return this.buildCo2PerActionSankeyData("plan_co2_energy", "plan_co2", "Geplant");
    },
    referenceCo2Sankey() {
      return this.buildCo2TotalSankeyData(
        this.totalReferenceCo2, "Referenz", this.referenceCo2ByFuel);
    },
    plannedCo2Sankey() {
      return this.buildCo2TotalSankeyData(
        this.totalPlannedCo2, "Geplant", this.plannedCo2ByFuel);
    },
    /*
     * Deckungsgrad je Energieträger: wie viel des **gemessenen** Verbrauchs
     * bilden die erfassten Maßnahmen ab?
     *
     * Baseline ist gezählt, Referenz ist gerechnet. Unter 100 % ist der
     * Normalfall — die Maßnahmen decken einen Teil des Standorts. Über 100 %
     * heisst, das Modell verbraucht mehr als die Zähler hergeben; jede daraus
     * abgeleitete Einsparung ist dann zu gross.
     */
    coverageRows() {
      return this.energyConsumptionTableRows
        .filter((row) => row.baseline > 0 || row.reference > 0)
        .map((row) => ({
          ...row,
          coverage: row.baseline > 0 ? (row.reference / row.baseline) * 100 : 0,
        }));
    },
    /*
     * Die Träger, bei denen die Rechnung die Messung übersteigt.
     *
     * Ein Prozentpunkt Toleranz: Baseline und Referenz stammen aus
     * verschiedenen Quellen, und eine Punktlandung ist nicht zu erwarten.
     * Gemeldet wird, was darüber hinausgeht.
     */
    coverageOverruns() {
      return this.coverageRows.filter((row) => row.baseline > 0 && row.coverage > 101);
    },
    coverageChartData() {
      const zeilen = this.coverageRows;
      return {
        labels: zeilen.map((row) => row.name),
        datasets: [
          {
            label: "Baseline (gemessen)",
            data: zeilen.map((row) => Math.round(row.baseline)),
            backgroundColor: "rgba(69, 90, 100, 0.75)",
            borderColor: "#455a64",
            borderWidth: 1,
          },
          {
            label: "Referenz (gerechnet)",
            data: zeilen.map((row) => Math.round(row.reference)),
            backgroundColor: "rgba(239, 143, 132, 0.8)",
            borderColor: "#ef8f84",
            borderWidth: 1,
          },
          {
            label: "Geplant (gerechnet)",
            data: zeilen.map((row) => Math.round(row.planned)),
            backgroundColor: "rgba(123, 192, 67, 0.8)",
            borderColor: "#7bc043",
            borderWidth: 1,
          },
        ],
      };
    },
    coverageChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: true, position: "bottom" },
        plugins: { datalabels: false },
        scales: {
          yAxes: [
            {
              ticks: {
                callback(value) {
                  return new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 })
                    .format(value);
                },
              },
              scaleLabel: { display: true, labelString: "Endenergie [kWh/a]" },
            },
          ],
        },
      };
    },
    /*
     * Die Einsparung je Energieträger, die die Prognose von der Baseline
     * abzieht. Referenz minus Geplant — also das, was die Maßnahmen an
     * Endenergie herausholen.
     */
    savingsByFuelForForecast() {
      const einsparung = {};
      for (const row of this.energyConsumptionTableRows) {
        const wert = Number(row.savings) || 0;
        if (wert !== 0) {
          einsparung[Number(row.id)] = wert;
        }
      }
      return einsparung;
    },
    /*
     * Die **Emissions**einsparung je Energieträger, in Tonnen CO₂e.
     *
     * Referenz minus Geplant aus der Aufteilung des Servers
     * (`ref_co2_energy`/`plan_co2_energy`), also aus derselben Rechnung wie
     * die Gesamtzahlen. Hier aus Mengen und Faktoren neu zu rechnen wäre eine
     * zweite Emissionsrechnung neben der bestehenden — und die Gutschriften
     * für selbst verbrauchten erneuerbaren Strom liessen sich dabei gar nicht
     * nachbilden.
     *
     * Umgerechnet in Tonnen: die Faktoren stehen in Gramm je Kilowattstunde
     * (Netzstrom 427, Erdgas 182), die Rohwerte sind also Gramm.
     *
     * Zusammengefasst über `kanonischeId`, damit Nutzenergie Strom und die
     * PV-Gutschrift auf demselben Träger landen wie der gemessene Strom.
     *
     * Was auf eine **Nutz**energie entfällt — Wärme (11), Kälte (12) — hat im
     * Verbrauchskatalog keinen eigenen Träger: Wärme entsteht aus Erdgas oder
     * Fernwärme. Diese Posten erscheinen deshalb in der Summenreihe der
     * Regression, aber in keiner Trägerreihe. Das ist richtig so; eine
     * erfundene Zuordnung wäre schlechter als keine.
     */
    co2SavingsByFuel() {
      const referenz = this.totalResult.ref_co2_energy || {};
      const geplant = this.totalResult.plan_co2_energy || {};
      const ids = new Set([...Object.keys(referenz), ...Object.keys(geplant)]);

      const einsparung = {};
      for (const id of ids) {
        const traeger = kanonischeId(id);
        const delta = (Number(referenz[id]) || 0) - (Number(geplant[id]) || 0);
        einsparung[traeger] = (einsparung[traeger] || 0) + delta;
      }

      const inTonnen = {};
      for (const [traeger, gramm] of Object.entries(einsparung)) {
        if (gramm !== 0) {
          inTonnen[Number(traeger)] = gramm / 1e6;
        }
      }
      return inTonnen;
    },
    /** Das letzte Jahr mit gemessenem Verbrauch — Startpunkt der Prognose. */
    lastBaselineYear() {
      const jahre = (this.project?.energy_consumption_data || [])
        .map((eintrag) => Number(eintrag.year))
        .filter((jahr) => Number.isFinite(jahr));
      return jahre.length ? Math.max(...jahre) : null;
    },
    hasEnergyMixData() {
      return this.energyConsumptionTableRows.some((row) => row.reference > 0 || row.planned > 0);
    },
    energyMixLabels() {
      return this.energyConsumptionTableRows.map((row) => row.name);
    },
    energyMixColors() {
      return this.energyMixLabels.map((_, index) => this.toRgba(this.pickSeriesColor(index), 0.82));
    },
    energyMixReferenceChartData() {
      return {
        labels: this.energyMixLabels,
        datasets: [
          {
            label: "Referenz [kWh]",
            data: this.energyConsumptionTableRows.map((row) => Math.round(Number(row.reference) || 0)),
            backgroundColor: this.energyMixColors,
            borderWidth: 1,
            borderColor: "#ffffff",
          },
        ],
      };
    },
    energyMixPlannedChartData() {
      return {
        labels: this.energyMixLabels,
        datasets: [
          {
            label: "Geplant [kWh]",
            data: this.energyConsumptionTableRows.map((row) => Math.round(Number(row.planned) || 0)),
            backgroundColor: this.energyMixColors,
            borderWidth: 1,
            borderColor: "#ffffff",
          },
        ],
      };
    },
    energyMixChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: "bottom",
        },
        tooltips: {
          callbacks: {
            label(tooltipItem, data) {
              const dataset = data.datasets?.[tooltipItem.datasetIndex] || {};
              const value = Number(dataset.data?.[tooltipItem.index]) || 0;
              const total = (dataset.data || []).reduce((sum, item) => sum + (Number(item) || 0), 0);
              const share = total > 0 ? (value / total) * 100 : 0;
              const label = data.labels?.[tooltipItem.index] || "";
              return `${label}: ${new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(value)} kWh (${new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(share)} %)`;
            },
          },
        },
      };
    },
    referenceSankey() {
      return this.buildSankeyData("energyRef");
    },
    plannedSankey() {
      return this.buildSankeyData("energyPlan");
    },
    referencePerActionSankey() {
      return this.buildPerActionSankeyData("energyRef");
    },
    plannedPerActionSankey() {
      return this.buildPerActionSankeyData("energyPlan");
    },
  },
  watch: {
    actionList(actionList) {
      if (Array.isArray(actionList)) {
        this.calcActions();
      }
    },
    calcResponse() {
      // Keep the shared docx chart cache populated so the DIN 16247 / appendix
      // reports (triggered from other views) can embed the energy-flow diagrams.
      this.scheduleDocxChartCache();
    },
    project: {
      handler(project) {
        if (project) {
          this.store.setTitle(project.name);
        }
      },
      immediate: true,
    },
  },
  beforeMount() {
    this.fetch().then(() => this.calcActions());
    this.fetchEnergyNames();
  },
  methods: {
    fetch() {
      return this.store.fetchCurrentProject(this.projectId);
    },
    fetchEnergyNames() {
      api
        .get("/projects/fuels/")
        .then((response) => {
          for (const energy of response.data || []) {
            this.fuels[Number(energy.id)] = energy.name;
          }
        })
        .catch(() => {
          toast.warning("Energieträger konnten nicht geladen werden.");
        });
    },
    getEnergyName(energyId) {
      const id = Number(energyId);
      /*
       * `flowNamesById` als dritter Griff, vor dem Rueckfall.
       *
       * `this.fuels` ist der Katalog der **Endenergie**traeger aus
       * `/projects/fuels/`. Die Nutzenergien (Strom 10, Waerme 11, Kaelte 12)
       * stehen nicht darin, und fuer sie stand bisher „Energie 10" im
       * Diagramm. Der Rechenkern schickt zu jedem Fluss ohnehin seinen Namen
       * mit (`Flow.to_dict`: `{id, name, quantity}`, gespeist aus
       * `EnergyIDs.names`) — den nehmen wir, statt die Tabelle hier ein
       * zweites Mal zu pflegen.
       */
      return this.customEnergyNames[id]
        || this.fuels[id]
        || this.flowNamesById[id]
        || `Energie ${energyId}`;
    },

    /*
     * Der Name eines Flusses. `flow.name` kommt aus dem statischen Katalog des
     * Rechenkerns und heisst bei den frei benennbaren Wärmeträgern „Sonstige 2".
     * Der Projektname geht deshalb vor — sonst stünde im Sankey-Diagramm ein
     * anderer Träger als im Formular, obwohl es derselbe ist.
     */
    /*
     * Die Summe eines Sankey-Diagramms.
     *
     * Bewusst aus den gezeichneten Bändern gerechnet und nicht aus einer
     * danebenliegenden Kennzahl: eine Summe an der Grafik, die nicht der Grafik
     * entspricht, ist schlimmer als gar keine.
     *
     * Gezählt werden die Bänder, die in einem Endknoten ankommen — einem
     * Knoten, der selbst nirgends Quelle ist. Alles andere wäre je nach
     * Diagrammart falsch: in der Maßnahmenansicht läuft jede Energiemenge
     * zweimal über das Bild (Träger → Maßnahme, Maßnahme → Nutzen), eine
     * schlichte Summe aller Bänder zählte sie doppelt. Über die Endknoten
     * gerechnet liefern Maßnahmen- und Summenansicht desselben Szenarios
     * denselben Wert.
     */
    sankeySumme(diagramm) {
      const links = diagramm?.links || [];
      const istQuelle = new Set(links.map((link) => link.source));
      return links
        .filter((link) => !istQuelle.has(link.target))
        .reduce((summe, link) => summe + (Number(link.value) || 0), 0);
    },

    /* Summe der gezeichneten Segmente eines Kreisdiagramms. */
    kreisSumme(chartData) {
      return (chartData?.datasets || []).reduce(
        (summe, datensatz) => summe + (datensatz.data || []).reduce(
          (teil, wert) => teil + (Number(wert) || 0), 0,
        ),
        0,
      );
    },

    energyNameFor(flow) {
      return this.customEnergyNames[Number(flow.id)]
        || flow.name
        || this.getEnergyName(flow.id);
    },
    getScenarioEnergyValue(entry, energyId, scenario) {
      const map = scenario === "planned" ? entry?.plannedByEnergy : entry?.referenceByEnergy;
      return Number(map?.[energyId]) || 0;
    },
    formatNumber(value) {
      return new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(Number(value) || 0);
    },
    formatPercent(value) {
      return `${new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 }).format(Number(value) || 0)} %`;
    },
    calcActionsCalcList() {
      /*
       * `forCalc` ist die Auswahl, die das Häkchen „Maßnahme berechnen" auf
       * der Maßnahmenseite setzt; sie kommt vom Server (`Action.checked`).
       *
       * Vorher wurde hier gegen `noCalc` gefiltert — eine ältere, rein lokale
       * Liste, die niemand mehr füllt. Der Filter lief also immer ins Leere und
       * diese Ansicht rechnete **alle** Maßnahmen, auch die abgewählten. Das
       * Einsparpotential war die einzige Ansicht, die es richtig gemacht hat,
       * weil es `forCalc` verwendet.
       */
      const checked = new Set(this.store.forCalc);
      this.actionCalcList = this.actionList
        .map((item) => item.id)
        .filter((id) => checked.has(id));
    },
    calcConsumerMitGenList() {
      const actionsForCalc = this.actionList.filter((item) => this.actionCalcList.includes(item.id));
      const result = {};

      actionsForCalc.forEach((item) => {
        if (Array.isArray(item.generator_type) && item.generator_type.length > 0) {
          result[item.id] = (result[item.id] || []).concat(item.generator_type);
        }
      });
      return result;
    },
    calcActions() {
      this.calcActionsCalcList();
      this.consMitGens = this.calcConsumerMitGenList();
      api
        .post(`/projects/${this.projectId}/calc/`, {
          params: { actions: this.actionCalcList, ConsMitGen_ids: this.consMitGens },
        })
        .then((response) => {
          this.calcResponse = response.data;
          this.collapsedGroupIds = (response.data?.result?.actionGroups || []).map((group) => group.id);
          this.warningResponse = response.data?.result?.warnings || [];
          this.errorResponse = null;
        })
        .catch((error) => {
          const detail = error.response?.data;
          this.errorResponse = Array.isArray(detail)
            ? detail.join(" ")
            : detail?.detail || "Die Energiebilanz konnte nicht berechnet werden.";
        });
    },
    normalizeFlows(flows) {
      return (flows || []).map((flow) => ({
        id: Number(flow.id),
        name: flow.name,
        quantity: Number(flow.quantity) || 0,
      }));
    },
    mapFlowsById(flows) {
      return this.normalizeFlows(flows).reduce((acc, flow) => {
        if (flow.quantity >= 0) {
          return acc;
        }
        acc[flow.id] = (acc[flow.id] || 0) + Math.abs(flow.quantity);
        return acc;
      }, {});
    },
    isGroupExpanded(groupId) {
      return !this.collapsedGroupIds.includes(groupId);
    },
    toggleGroup(groupId) {
      const next = [...this.collapsedGroupIds];
      const index = next.indexOf(groupId);
      if (index > -1) {
        next.splice(index, 1);
      } else {
        next.push(groupId);
      }
      this.collapsedGroupIds = next;
    },
    pickSeriesColor(index) {
      const palette = [
        "#1565c0",
        "#2e7d32",
        "#6a1b9a",
        "#ef6c00",
        "#00838f",
        "#ad1457",
        "#5d4037",
        "#424242",
      ];
      return palette[index % palette.length];
    },
    toRgba(hexColor, alpha) {
      const sanitized = (hexColor || "").replace("#", "");
      if (sanitized.length !== 6) {
        return `rgba(66,66,66,${alpha})`;
      }
      const red = parseInt(sanitized.substring(0, 2), 16);
      const green = parseInt(sanitized.substring(2, 4), 16);
      const blue = parseInt(sanitized.substring(4, 6), 16);
      return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    },
    /**
     * Die Emissionsposten eines Ergebnisses, auf Träger zusammengefasst.
     *
     * Fasst die Ids zusammen, die denselben Träger meinen, und wirft weg, was
     * unterm Strich nichts beiträgt — siehe `KANONISCHE_ENERGIE`.
     *
     * @param {Record<string, number>} breakdown `ref_co2_energy` o. ä.
     * @returns {Array<[number, number]>} Paare aus Energie-Id und CO₂e,
     *   absteigend nach Beitrag.
     */
    co2NachTraeger(breakdown) {
      const nachId = new Map();
      for (const [id, co2] of Object.entries(breakdown || {})) {
        const betrag = Number(co2);
        if (!Number.isFinite(betrag) || betrag === 0) {
          continue;
        }
        const quelle = Number(id);
        const traeger = KANONISCHE_ENERGIE[quelle] ?? quelle;
        nachId.set(traeger, (nachId.get(traeger) || 0) + betrag);
      }
      return Array.from(nachId.entries())
        .filter(([, co2]) => co2 > 0)
        .sort((a, b) => b[1] - a[1]);
    },
    /**
     * Das Sankey „CO2e-Bilanz je Maßnahme": Energieträger → Maßnahme → Summe.
     *
     * Vorher lief alles aus einem einzigen Knoten „Referenz CO2e-Quelle" in
     * die Maßnahmen. Die linke Seite trug damit keine Aussage: man sah, wie
     * viel jede Maßnahme emittiert, aber nicht **woraus**. Das ist dieselbe
     * Lücke, die die Summenkarte daneben hatte — und die Energiebilanz-Karten
     * darüber zeigen ihre Träger längst.
     *
     * Die Aufteilung kommt je Maßnahme fertig vom Server
     * (`ActionResult.ref_co2_energy`), gebildet über dieselbe Rechnung wie die
     * Gesamtzahl der Maßnahme. Sie selbst aus Mengen und Faktoren zu bilden
     * ginge nicht: der Strombedarf kommt als Nutzenergie herein und hat im
     * Projekt keinen Faktor, und Gutschriften für erneuerbaren Strom sind von
     * aussen nicht nachzubilden.
     *
     * Der Abfluss einer Maßnahme ist die Summe ihrer Zuflüsse, nicht ihr
     * `ref_co2`. Die beiden können auseinanderliegen, wenn eine Gutschrift
     * einen Träger unter null drückt und er dadurch entfällt; ein Sankey, das
     * mehr abführt als zufliesst, wäre schlicht falsch gezeichnet.
     *
     * Ohne Aufteilung — etwa bei einem älteren Ergebnis aus der Sitzung —
     * bleibt es beim bisherigen Sammelknoten.
     */
    buildCo2PerActionSankeyData(breakdownKey, valueKey, scenarioLabel) {
      const sammelQuelle = `${scenarioLabel} CO2e-Quelle`;
      const targetNode = `${scenarioLabel} CO2e gesamt`;
      const links = [];
      const actionNodes = [];
      const traegerNodes = [];

      this.cleanActionResults.forEach((action) => {
        const actionName = action.name || `Maßnahme ${action.id}`;
        const traeger = this.co2NachTraeger(action?.[breakdownKey]);

        if (traeger.length) {
          let zufluss = 0;
          traeger.forEach(([id, co2]) => {
            const name = this.getEnergyName(id);
            traegerNodes.push(name);
            links.push({ source: name, target: actionName, value: co2 });
            zufluss += co2;
          });
          actionNodes.push(actionName);
          links.push({ source: actionName, target: targetNode, value: zufluss });
          return;
        }

        const value = Math.abs(Number(action?.[valueKey] ?? action?.total?.[valueKey]) || 0);
        if (value <= 0) {
          return;
        }
        traegerNodes.push(sammelQuelle);
        actionNodes.push(actionName);
        links.push({ source: sammelQuelle, target: actionName, value });
        links.push({ source: actionName, target: targetNode, value });
      });

      if (!links.length) {
        return { nodes: [], links: [] };
      }

      const nodes = [
        ...Array.from(new Set(traegerNodes)).map((name) => ({ name, nodeType: "ee" })),
        ...Array.from(new Set(actionNodes)).map((name) => ({ name, nodeType: "action" })),
        { name: targetNode, nodeType: "ne" },
      ];

      return { nodes, links };
    },
    /**
     * Das Summen-Sankey der CO₂e-Bilanz, aufgeteilt nach Energieträger.
     *
     * Vorher stand hier ein einziger Strang „Referenz CO2e" → „CO2e gesamt"
     * mit dem Gesamtwert. Als Flussbild sagte das nichts: man sah die Menge,
     * aber nicht, woher sie kommt. Gerade das ist bei CO₂ die Frage — 1 kWh
     * Erdgas und 1 kWh Strom wiegen völlig verschieden, und welcher Träger die
     * Emissionen treibt, entscheidet, welche Maßnahme überhaupt etwas bringt.
     *
     * Gutschriften werden auf den Träger geschlagen, den sie verdrängen
     * (`KANONISCHE_ENERGIE`): der selbst verbrauchte PV-Strom steht als
     * eigener, negativer Posten „Erneuerbarer Strom" in der Aufteilung und
     * mindert den Strang „Strom", statt als nicht zeichenbarer negativer
     * Fluss zu verschwinden. Am Demo-Projekt: 237,7 Mio. − 133,2 Mio. =
     * 104,4 Mio.
     *
     * Über die Id und nicht über den Anzeigenamen: Id 21 heisst „Erneuerbarer
     * Strom", nicht „Strom". Eine Gruppierung nach Namen liess die Gutschrift
     * durchfallen — sie wurde als negativer Posten verworfen und die Summe
     * war um sie zu hoch.
     *
     * Was danach null oder negativ bleibt, entfällt — ein Träger, der unterm
     * Strich nichts beiträgt, gehört nicht als Band ins Bild.
     *
     * Nicht skaliert. Eine frühere Fassung rechnete die Stränge auf den
     * Gesamtwert hoch, damit die Karte keine kleinere Summe zeigt als ihre
     * Kopfzeile. Das war ein stiller Fehler: der Anteil eines Trägers ohne
     * Faktor wurde den übrigen zugeschlagen — so landeten die Stromemissionen
     * beim Erdgas. Nötig ist die Skalierung ohnehin nicht mehr, weil die
     * Aufteilung jetzt vom Server kommt und sich exakt zur Summe addiert.
     */
    buildCo2TotalSankeyData(totalValue, scenarioLabel, co2ByEnergy) {
      const value = Math.abs(Number(totalValue) || 0);
      if (value <= 0) {
        return { nodes: [], links: [] };
      }

      const target = "CO2e gesamt";
      const eintraege = this.co2NachTraeger(co2ByEnergy);

      if (!eintraege.length) {
        const source = `${scenarioLabel} CO2e`;
        return {
          nodes: [
            { name: source, nodeType: "ee" },
            { name: target, nodeType: "ne" },
          ],
          links: [{ source, target, value }],
        };
      }

      // Der Name erst ganz am Ende, wenn feststeht, welche Ids zusammengehören.
      const benannt = eintraege.map(([id, co2]) => [this.getEnergyName(id), co2]);
      const nodes = benannt.map(([name]) => ({ name, nodeType: "ee" }));
      nodes.push({ name: target, nodeType: "ne" });

      const links = benannt.map(([name, co2]) => ({ source: name, target, value: co2 }));

      return { nodes, links };
    },
    buildSankeyData(flowKey) {
      const linkMap = new Map();
      const eeNodeSet = new Set();
      // Als Map, weil die Ausgangsseite seit den Verlusten zwei Knotenarten
      // kennt.
      const neNodeSet = new Map();

      this.actionResults.forEach((action) => {
        const flows = this.normalizeFlows(action?.[flowKey]);
        const sourceFlows = flows
          .filter((flow) => flow.quantity < 0)
          .map((flow) => ({ ...flow, quantity: Math.abs(flow.quantity) }));
        const targetFlows = flows.filter((flow) => flow.quantity > 0);

        if (!sourceFlows.length || !targetFlows.length) {
          return;
        }

        const sourceTotal = sourceFlows.reduce((sum, flow) => sum + flow.quantity, 0);
        if (sourceTotal <= 0) {
          return;
        }

        targetFlows.forEach((targetFlow) => {
          const targetName = this.energyNameFor(targetFlow);
          neNodeSet.set(targetName, sankeyTargetType(targetFlow.id));

          sourceFlows.forEach((sourceFlow) => {
            const sourceName = this.energyNameFor(sourceFlow);
            eeNodeSet.add(sourceName);

            const value = targetFlow.quantity * (sourceFlow.quantity / sourceTotal);
            if (value <= 0) {
              return;
            }

            const key = `${sourceName}|||${targetName}`;
            const current = linkMap.get(key) || 0;
            linkMap.set(key, current + value);
          });
        });
      });

      const links = Array.from(linkMap.entries()).map(([key, value]) => {
        const [source, target] = key.split("|||");
        return {
          source,
          target,
          value,
        };
      });

      const nodes = [
        ...Array.from(eeNodeSet).map((name) => ({ name, nodeType: "ee" })),
        ...Array.from(neNodeSet.entries()).map(([name, nodeType]) => ({ name, nodeType })),
      ];

      return { nodes, links };
    },
    buildPerActionSankeyData(flowKey) {
      const linkMap = new Map();
      const nodeSet = new Map();

      const addNode = (name, nodeType) => {
        if (!nodeSet.has(name)) {
          nodeSet.set(name, nodeType);
        }
      };

      const addLink = (source, target, value) => {
        if (value <= 0) {
          return;
        }
        const key = `${source}|||${target}`;
        linkMap.set(key, (linkMap.get(key) || 0) + value);
      };

      this.actionResults.forEach((action) => {
        if (Array.isArray(action.errors) && action.errors.length > 0) {
          return;
        }

        // Linked generators are rendered under their consumer action only.
        if (action.isLinkedGenerator) {
          return;
        }

        const actionName = action.name || `Maßnahme ${action.id}`;
        const flows = this.normalizeFlows(action?.[flowKey]);
        const sourceFlows = flows.filter((flow) => flow.quantity < 0);
        const targetFlows = flows.filter((flow) => flow.quantity > 0);

        if (!sourceFlows.length && !targetFlows.length) {
          return;
        }

        addNode(actionName, "action");

        sourceFlows.forEach((flow) => {
          const sourceName = this.energyNameFor(flow);
          addNode(sourceName, "ee");
          addLink(sourceName, actionName, Math.abs(flow.quantity));
        });

        targetFlows.forEach((flow) => {
          const targetName = this.energyNameFor(flow);
          addNode(targetName, sankeyTargetType(flow.id));
          addLink(actionName, targetName, flow.quantity);
        });

        const linkedGenerators = action.linkedGenerators || [];
        linkedGenerators.forEach((generator) => {
          if (Array.isArray(generator.errors) && generator.errors.length > 0) {
            return;
          }

          const generatorName = generator.name || `Generator ${generator.id}`;
          const generatorFlows = this.normalizeFlows(generator?.[flowKey]);
          const generatorSources = generatorFlows.filter((flow) => flow.quantity < 0);
          const generatorTargets = generatorFlows.filter((flow) => flow.quantity > 0);

          if (!generatorSources.length && !generatorTargets.length) {
            return;
          }

          addNode(generatorName, "generator");

          generatorSources.forEach((flow) => {
            const sourceName = this.energyNameFor(flow);
            addNode(sourceName, "ee");
            addLink(sourceName, generatorName, Math.abs(flow.quantity));
          });

          generatorTargets.forEach((flow) => {
            const targetName = this.energyNameFor(flow);
            const generatorOutputNode = `${targetName} (gen)`;

            addNode(generatorOutputNode, "ne-gen");
            addLink(generatorName, generatorOutputNode, flow.quantity);
            addLink(generatorOutputNode, actionName, flow.quantity);
          });
        });
      });

      const links = Array.from(linkMap.entries()).map(([key, value]) => {
        const [source, target] = key.split("|||");
        return { source, target, value };
      });

      const nodes = Array.from(nodeSet.entries()).map(([name, nodeType]) => ({ name, nodeType }));

      return { nodes, links };
    },
    getSankeyComponentByRef(refName) {
      const component = this.$refs[refName];
      if (!component) {
        return null;
      }
      if (Array.isArray(component)) {
        return component[0] || null;
      }
      return component;
    },
    getChartByRef(refName) {
      const chartComponent = this.$refs[refName];
      if (!chartComponent) {
        return null;
      }
      if (Array.isArray(chartComponent)) {
        const instance = chartComponent[0];
        return instance?.chart || (instance?.$data && instance.$data._chart) || instance?._chart || null;
      }
      return chartComponent?.chart || (chartComponent?.$data && chartComponent.$data._chart) || chartComponent?._chart || null;
    },
    downloadChartImageByRef(refName, filePrefix) {
      const chart = this.getChartByRef(refName);
      if (!chart) {
        toast.warning("Grafik ist noch nicht bereit. Bitte erneut versuchen.");
        return;
      }

      try {
        const link = document.createElement("a");
        link.download = `${filePrefix}-projekt-${this.projectId}.png`;

        if (chart.canvas && typeof chart.canvas.toBlob === "function") {
          chart.canvas.toBlob((blob) => {
            if (!blob) {
              toast.error("Grafik konnte nicht erstellt werden.");
              return;
            }
            const url = URL.createObjectURL(blob);
            link.href = url;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }, "image/png");
          return;
        }

        const dataUrl = chart.toBase64Image();
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch {
        toast.error("Grafik konnte nicht heruntergeladen werden.");
      }
    },
    exportChartDataExcelGeneric(chartData, filePrefix) {
      return exportChartExcel(this.projectId, chartData, filePrefix);
    },
    downloadSankeyImageByRef(refName, filePrefix) {
      const component = this.getSankeyComponentByRef(refName);
      const svgElement = component?.$el?.querySelector("svg");
      if (!svgElement) {
        toast.warning("Grafik ist noch nicht bereit. Bitte erneut versuchen.");
        return;
      }

      try {
        const serializer = new XMLSerializer();
        const svgMarkup = serializer.serializeToString(svgElement);
        const svgBlob = new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" });
        const svgUrl = URL.createObjectURL(svgBlob);

        const image = new Image();
        image.onload = () => {
          const width = Number(svgElement.viewBox?.baseVal?.width) || svgElement.clientWidth || 1200;
          const height = Number(svgElement.viewBox?.baseVal?.height) || svgElement.clientHeight || 700;
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const context = canvas.getContext("2d");
          if (!context) {
            URL.revokeObjectURL(svgUrl);
            toast.error("Grafik konnte nicht erstellt werden.");
            return;
          }

          context.fillStyle = "#ffffff";
          context.fillRect(0, 0, width, height);
          context.drawImage(image, 0, 0);

          canvas.toBlob((blob) => {
            URL.revokeObjectURL(svgUrl);
            if (!blob) {
              toast.error("Grafik konnte nicht erstellt werden.");
              return;
            }
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${filePrefix}-projekt-${this.projectId}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }, "image/png");
        };
        image.onerror = () => {
          URL.revokeObjectURL(svgUrl);
          toast.error("Grafik konnte nicht heruntergeladen werden.");
        };
        image.src = svgUrl;
      } catch {
        toast.error("Grafik konnte nicht heruntergeladen werden.");
      }
    },
    exportSankeyDataExcel(sankeyData, filePrefix) {
      return exportSankeyExcel(this.projectId, sankeyData, filePrefix);
    },
    chartToBase64(chart) {
      return new Promise((resolve, reject) => {
        if (!chart) {
          resolve(null);
          return;
        }

        if (chart.canvas && typeof chart.canvas.toBlob === "function") {
          chart.canvas.toBlob((blob) => {
            if (!blob) {
              resolve(null);
              return;
            }
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          }, "image/png");
          return;
        }

        if (typeof chart.toBase64Image === "function") {
          resolve(chart.toBase64Image());
          return;
        }

        resolve(null);
      });
    },
    sankeyRefToBase64(refName) {
      return new Promise((resolve) => {
        const component = this.getSankeyComponentByRef(refName);
        const svgElement = component?.$el?.querySelector("svg");
        if (!svgElement) {
          resolve(null);
          return;
        }

        try {
          const serializer = new XMLSerializer();
          const svgMarkup = serializer.serializeToString(svgElement);
          const svgBlob = new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" });
          const svgUrl = URL.createObjectURL(svgBlob);
          const image = new Image();

          image.onload = () => {
            const width = Number(svgElement.viewBox?.baseVal?.width) || svgElement.clientWidth || 1200;
            const height = Number(svgElement.viewBox?.baseVal?.height) || svgElement.clientHeight || 700;
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const context = canvas.getContext("2d");
            if (!context) {
              URL.revokeObjectURL(svgUrl);
              resolve(null);
              return;
            }

            context.fillStyle = "#ffffff";
            context.fillRect(0, 0, width, height);
            context.drawImage(image, 0, 0);

            canvas.toBlob((blob) => {
              URL.revokeObjectURL(svgUrl);
              if (!blob) {
                resolve(null);
                return;
              }
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = () => resolve(null);
              reader.readAsDataURL(blob);
            }, "image/png");
          };

          image.onerror = () => {
            URL.revokeObjectURL(svgUrl);
            resolve(null);
          };
          image.src = svgUrl;
        } catch {
          resolve(null);
        }
      });
    },
    scheduleDocxChartCache() {
      if (this.docxCacheTimer) {
        window.clearTimeout(this.docxCacheTimer);
      }
      // wait for the sankeys/charts to (re)render before capturing them
      this.docxCacheTimer = window.setTimeout(() => {
        this.cacheEnergyChartsForDocx();
      }, 800);
    },
    async cacheEnergyChartsForDocx() {
      try {
        await this.$nextTick();
        const charts = await this.collectEnergyChartsForDocx();
        // Only overwrite the cache when something actually rendered, so a not-yet-drawn
        // view can't wipe previously captured (good) charts with empty entries.
        if (charts.some((chart) => chart.imageBase64)) {
          storeDocxModuleCharts(this.projectId, "Energiebilanz", charts);
        }
      } catch {
        // best-effort caching; ignore failures
      }
    },
    async collectEnergyChartsForDocx() {
      const descriptors = [
        { id: "energyMixReferenceChart", title: "Energie-Mix Referenz", module: "Energiebilanz", type: "chart" },
        { id: "energyMixPlannedChart", title: "Energie-Mix Geplant", module: "Energiebilanz", type: "chart" },
        { id: "energyRefActionSankey", title: "Energiebilanz Referenz (Maßnahmen)", module: "Energiebilanz", type: "sankey" },
        { id: "energyPlanActionSankey", title: "Energiebilanz Geplant (Maßnahmen)", module: "Energiebilanz", type: "sankey" },
        { id: "energyRefTotalSankey", title: "Energiebilanz Referenz (Summe)", module: "Energiebilanz", type: "sankey" },
        { id: "energyPlanTotalSankey", title: "Energiebilanz Geplant (Summe)", module: "Energiebilanz", type: "sankey" },
        { id: "co2RefActionSankey", title: "CO2e-Bilanz Referenz (Maßnahmen)", module: "Energiebilanz", type: "sankey" },
        { id: "co2PlanActionSankey", title: "CO2e-Bilanz Geplant (Maßnahmen)", module: "Energiebilanz", type: "sankey" },
        { id: "co2RefTotalSankey", title: "CO2e-Bilanz Referenz (Summe)", module: "Energiebilanz", type: "sankey" },
        { id: "co2PlanTotalSankey", title: "CO2e-Bilanz Geplant (Summe)", module: "Energiebilanz", type: "sankey" },
      ];

      const charts = [];
      for (const descriptor of descriptors) {
        let imageBase64 = null;
        if (descriptor.type === "sankey") {
          imageBase64 = await this.sankeyRefToBase64(descriptor.id);
        } else {
          const chart = this.getChartByRef(descriptor.id);
          imageBase64 = await this.chartToBase64(chart);
        }

        charts.push({
          id: descriptor.id,
          title: descriptor.title,
          module: descriptor.module,
          imageBase64,
        });
      }

      return charts;
    },
    async exportDocxWithAppendix() {
      this.isDocxExporting = true;

      try {
        await this.$nextTick();
        await new Promise((resolve) => window.setTimeout(resolve, 350));

        const charts = await this.collectEnergyChartsForDocx();
        storeDocxModuleCharts(this.projectId, "Energiebilanz", charts);
        const response = await api.post(
          `/projects/${this.projectId}/docx_extended/`,
          {
            charts,
            meta: {
              source: "energiebilanz",
              schemaVersion: "1.0",
              exportedAt: new Date().toISOString(),
            },
          },
          { responseType: "blob" },
        );

        const url = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        const disposition = response.headers?.["content-disposition"] || "";
        const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        link.download = match?.[1]?.replace(/['"]/g, "") || `Einsparpotentialbericht_inkl_Anhang_${this.projectId}.docx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch {
        toast.error("DOCX Export konnte nicht erstellt werden.");
      } finally {
        this.isDocxExporting = false;
      }
    },
    async exportEnergyExcel() {
      this.isExporting = true;

      try {
        const response = await api.post(
          `/projects/${this.projectId}/energy_excel/`,
          {
            consumptionTable: this.energyConsumptionTableRows,
            perActionRef: this.perActionEnergyData.map((action) => ({
              name: action.name,
              energies: action.referenceByEnergy,
            })),
            perActionPlan: this.perActionEnergyData.map((action) => ({
              name: action.name,
              energies: action.plannedByEnergy,
            })),
            sankeyData: {
              "Energiebilanz Referenz (Maßnahmen)": this.referencePerActionSankey,
              "Energiebilanz Geplant (Maßnahmen)": this.plannedPerActionSankey,
              "Energiebilanz Referenz (Summe)": this.referenceSankey,
              "Energiebilanz Geplant (Summe)": this.plannedSankey,
              "CO2e-Bilanz Referenz (Maßnahmen)": this.referenceCo2PerActionSankey,
              "CO2e-Bilanz Geplant (Maßnahmen)": this.plannedCo2PerActionSankey,
              "CO2e-Bilanz Referenz (Summe)": this.referenceCo2Sankey,
              "CO2e-Bilanz Geplant (Summe)": this.plannedCo2Sankey,
            },
            energyColumns: this.perActionEnergyColumns,
            hasBaseline: this.hasBaseline,
          },
          { responseType: "blob" },
        );

        const url = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        const disposition = response.headers?.["content-disposition"] || "";
        const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        link.download = match?.[1]?.replace(/['"]/g, "") || `Energiebilanz-Projekt-${this.projectId}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch {
        toast.error("Energiebilanz konnte nicht exportiert werden.");
      } finally {
        this.isExporting = false;
      }
    },
  },
};
</script>

<style scoped>
.chart-section {
  width: 100%;
}

.chart-wrapper {
  position: relative;
  min-height: 360px;
}

.donut-wrapper {
  min-height: 340px;
}

.total-row {
  font-weight: bold;
}


/*
 * `whitesmoke` stand hier fest verdrahtet. Die Schrift der Zeile kommt aber aus
 * dem Thema (`on-surface`), und die ist im dunklen Modus hell — helle Schrift
 * auf fast weissem Grund. `surface-variant` ist dieselbe Absicht, nur als
 * Themenfarbe: eine leicht abgesetzte Flaeche, die in beiden Modi mitwandert.
 */
.group-row {
  background-color: rgb(var(--v-theme-surface-variant));
  font-weight: bold;
}
</style>


